import { useCallback, useMemo } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGetKubernetesClusters } from '../../../hooks';
import { KubernetesCluster } from './types';
import { serializeClusterEntity } from './utils';

export interface KubernetesClusterPickerProps {
  formData?: KubernetesCluster | string;
  onChange: (value: KubernetesCluster | string) => void;
  rawErrors?: string[];
  title?: string;
  description?: string;
  /** `'object'` emits the whole cluster; `'string'` emits its id. */
  type?: 'object' | 'string';
  /** Only offer clusters owned by one of the caller's own groups. */
  onlyUserOwned?: boolean;
  required?: boolean;
  id?: string;
}

const clusterLabel = (cluster: KubernetesCluster) =>
  cluster.title || cluster.name;

export const KubernetesClusterPicker = (
  props: KubernetesClusterPickerProps,
) => {
  const {
    formData,
    onChange,
    rawErrors,
    title = '',
    description = '',
    type = 'string',
    onlyUserOwned = false,
    required,
    id,
  } = props;

  const { value: entities, loading } = useGetKubernetesClusters(onlyUserOwned);

  const clusters = useMemo(
    () => entities?.map(entity => serializeClusterEntity(entity)) || [],
    [entities],
  );

  const onSelect = useCallback(
    (_: unknown, value: KubernetesCluster | null) => {
      if (!value) {
        onChange('');
        return;
      }

      onChange(type === 'object' ? value : value.id);
    },
    [onChange, type],
  );

  const selectedCluster = useMemo(() => {
    if (!formData) return null;

    if (typeof formData === 'string') {
      return clusters.find(cluster => cluster.id === formData) || null;
    }

    return clusters.find(cluster => cluster.name === formData.name) || null;
  }, [formData, clusters]);

  const hasErrors = Boolean(rawErrors?.length);

  return (
    <FormControl fullWidth required={required} error={hasErrors && !formData}>
      <Autocomplete
        size="small"
        id={id}
        value={selectedCluster}
        loading={loading}
        onChange={onSelect}
        options={clusters}
        getOptionLabel={clusterLabel}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="none"
            helperText={description}
            variant="outlined"
            required={required}
            error={hasErrors}
          />
        )}
        renderOption={({ key, ...optionProps }, option) => (
          <li key={key} {...optionProps}>
            <Box>
              <Typography variant="body2" style={{fontWeight: 600}} noWrap>
                {clusterLabel(option)}
              </Typography>
              {option.subscriptionId && (
                <Typography
                  variant="caption"
                  noWrap
                  color="text.secondary"
                  display="block"
                >
                  Subscription ID: {option.subscriptionId}
                </Typography>
              )}
              {option.resourceGroup && (
                <Typography
                  variant="caption"
                  noWrap
                  color="text.secondary"
                  display="block"
                >
                  Resource Group: {option.resourceGroup}
                </Typography>
              )}
            </Box>
          </li>
        )}
        noOptionsText="No clusters found"
        loadingText="Loading clusters..."
      />
      {rawErrors?.map((err, index) => (
        <FormHelperText key={index} error>
          {err}
        </FormHelperText>
      ))}
    </FormControl>
  );
};
